/**
 * Sites Step - Working sites configuration
 * Light theme, adds "Chantier" type for BTP clients
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Plus, Trash2, Building, Warehouse, Store, HardHat } from 'lucide-react';

// Pas de libellé ici : constante de module figée au chargement (piège n°1 i18n).
// Résolu au rendu via sites.types.<id> — clés partagées, pas de doublon local.
const SITE_TYPES = [
  { id: 'headquarters', icon: Building },
  { id: 'depot', icon: Warehouse },
  { id: 'branch', icon: Store },
  { id: 'chantier', icon: HardHat },
];

export function SitesStep({ data, updateData, onNext, onSkip }) {
  const { t } = useTranslation();
  const [newSite, setNewSite] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    type: 'depot',
  });
  const [showForm, setShowForm] = useState(false);

  const addSite = () => {
    if (newSite.name.trim()) {
      const sites = [...(data.sites || []), { ...newSite }];
      updateData({ sites });
      setNewSite({ name: '', address: '', city: '', postalCode: '', type: 'depot' });
      setShowForm(false);
    }
  };

  const removeSite = (index) => {
    const sites = data.sites.filter((_, i) => i !== index);
    updateData({ sites });
  };

  const getSiteIcon = (type) => {
    const siteType = SITE_TYPES.find(t => t.id === type);
    return siteType?.icon || MapPin;
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl">
          <MapPin className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {t('onboarding.sites.title')}
        </h2>
        <p className="text-gray-500">
          {t('onboarding.sites.subtitle')}
        </p>
      </div>

      {/* Default site from company info */}
      {data.companyCity && data.sites?.length === 0 && !showForm && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-gray-600 text-sm mb-3">
            {t('onboarding.sites.addHqPrompt')}
          </p>
          <button
            onClick={() => {
              updateData({
                sites: [{
                  // Nom de site : part en base, reste en français (référence, pas de l'UI)
                  name: `Siège - ${data.companyCity}`,
                  address: data.companyAddress,
                  city: data.companyCity,
                  postalCode: data.companyPostalCode,
                  type: 'headquarters',
                }]
              });
            }}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium transition-colors"
          >
            {t('onboarding.sites.addHqButton')}
          </button>
        </div>
      )}

      {/* Sites list */}
      {data.sites?.length > 0 && (
        <div className="space-y-3">
          {data.sites.map((site, index) => {
            const Icon = getSiteIcon(site.type);
            return (
              <div
                key={index}
                className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm"
              >
                <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-indigo-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-gray-900 font-medium truncate">{site.name}</h4>
                  <p className="text-gray-400 text-sm truncate">
                    {site.city || t('onboarding.sites.addressMissing')}
                  </p>
                </div>
                <button
                  onClick={() => removeSite(index)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add site form */}
      {showForm ? (
        <div className="p-4 bg-white border border-gray-300 rounded-xl space-y-4 shadow-sm animate-fade-in">
          <h4 className="text-gray-900 font-medium">{t('onboarding.sites.newSiteTitle')}</h4>

          {/* Site type */}
          <div className="flex flex-wrap gap-2">
            {SITE_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setNewSite(prev => ({ ...prev, type: type.id }))}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg transition-all
                    ${newSite.type === type.id
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{t(`sites.types.${type.id}`, { defaultValue: type.id })}</span>
                </button>
              );
            })}
          </div>

          {/* Name */}
          <input
            type="text"
            value={newSite.name}
            onChange={(e) => setNewSite(prev => ({ ...prev, name: e.target.value }))}
            placeholder={t('onboarding.sites.namePlaceholder')}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            autoFocus
          />

          {/* Address */}
          <input
            type="text"
            value={newSite.address}
            onChange={(e) => setNewSite(prev => ({ ...prev, address: e.target.value }))}
            placeholder={t('onboarding.sites.addressPlaceholder')}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />

          {/* City / Postal */}
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text"
              value={newSite.postalCode}
              onChange={(e) => setNewSite(prev => ({ ...prev, postalCode: e.target.value }))}
              placeholder={t('onboarding.sites.postalCodePlaceholder')}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            <input
              type="text"
              value={newSite.city}
              onChange={(e) => setNewSite(prev => ({ ...prev, city: e.target.value }))}
              placeholder={t('onboarding.sites.cityPlaceholder')}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowForm(false)}
              className="flex-1 px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={addSite}
              disabled={!newSite.name.trim()}
              className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.actions.add')}
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>{t('onboarding.sites.addSite')}</span>
        </button>
      )}

      {/* Summary */}
      {data.sites?.length > 0 && (
        <div className="text-center text-gray-400 text-sm">
          {t('onboarding.sites.configuredCount', { count: data.sites.length })}
        </div>
      )}

      {/* Skip hint */}
      <div className="text-center">
        <button
          onClick={onSkip}
          className="text-gray-400 hover:text-gray-600 text-sm underline transition-colors"
        >
          {t('onboarding.sites.skipHint')}
        </button>
      </div>
    </div>
  );
}

export default SitesStep;
